/* eslint-disable @typescript-eslint/no-non-null-assertion */
import 'react-native-get-random-values'

import { LinearGradient } from 'expo-linear-gradient'
import * as WebBrowser from 'expo-web-browser'
import { throttle } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useRef, useState } from 'react'
import { FlatList, Image, TouchableOpacity, View } from 'react-native'

import { GameStates, Player, PlayerStates } from '@ppwr/poker'

import calendar from '../../../assets/icons/calendar.png'
import createTournament from '../../../assets/menu/join.png'
import { ActionModal, Text } from '../../components'
import NavigationHeader from '../../components/NavigationHeader/NavigationHeader'
import { translate } from '../../i18n/translate'
import { useStore } from '../../models'
import {
  GameEntityModelType,
  TournamentAccessControl,
  TournamentDifficulty,
  TournamentModelType
} from '../../models/api-store'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import GameCard from './gameCard'
import styles from './styles'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'

const logger = extendLogger(LoggerContexts.ActiveGameScreen)

export const ActiveGameScreen = observer(() => {
  const router = useRouter()
  const store = useStore()
  const [activeGames, setActiveGames] = useState<GameEntityModelType[]>([])
  const [channelName, setChannelName] = useState<string>()
  const [visible, setVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [currentGameId, setCurrentGameId] = useState('')
  const [playerRole, setPlayerRole] = useState('')
  const timer = useRef<NodeJS.Timeout | undefined>(undefined)
  const [selectedDifficulty] = useState(TournamentDifficulty.ALL)
  const registeredGameActionTime = useRef<number>(0)
  const { accessControl } = useLocalSearchParams<{ accessControl: TournamentAccessControl }>()
  const hasAccessControl = accessControl ?? TournamentAccessControl.community
  const selectedCurrentTournament = useRef<GameEntityModelType | undefined>(undefined)

  const fetchActiveGameList = useCallback(
    async (selectedDifficulty: TournamentDifficulty) => {
      try {
        logger.info('fetchActiveGameList...', hasAccessControl, selectedDifficulty)
        const response = await store.game.getActiveTournament(hasAccessControl, selectedDifficulty)
        if (response) {
          logger.info('fetchActiveGameList fetched')
          setActiveGames(response)
        }
      } catch (error) {
        logger.error('Error in fetching the tournament', error)
      }
    },
    [hasAccessControl, store.game]
  )

  const onRegisterPrompt = async (isValidTime: boolean, isLocked: boolean, title: string) => {
    const isTeacher = store.session.isTeacher
    logger.info(`onRegisterPrompt store.game.setTableName(${title})`)
    store.game.setTableName(title)
    if (isTeacher && isValidTime && isLocked === false) {
      setVisible(true)
    } else {
      await goToGame()
    }
  }

  const onSelectRole = async (role: string) => {
    store.game.setPlayKind(role)
    setPlayerRole(role)
    setVisible(false)
    await goToGame()
  }

  const goToGame = async () => {
    try {
      if (!channelName) {
        throw new Error('Missing channelName')
      }

      store.communication.joinChannel(channelName)
      if (store.session.user) {
        // TODO : Match the user entity from dd
        //@ts-ignore
        store.telemetry.setUser({ ...store.session.user, gameId: store.game.gameId })
      }
      if (selectedCurrentTournament?.current?.tournament) {
        store.game.clearCurrentTournament()
        store.game.setCurrentTournament(selectedCurrentTournament.current.tournament as TournamentModelType)
      }
      store.game.setActionTime(registeredGameActionTime.current)
      // Note:- Reset game navigator before navigation to game screen
      router.replace('/game')
    } catch (error) {
      logger.error('Unable to fetch hash code', error)
    }
  }

  const setPlayKind = store.game.setPlayKind
  const teardown = store.game.teardown

  useFocusEffect(
    useCallback(() => {
      logger.info('useFocusEffect useCallback fetchActiveGameList')
      // Clear games when screen is focused to fetch and display latest
      setActiveGames([])
      fetchActiveGameList(selectedDifficulty)
      teardown()
      // We poll teardown in Tournament lobby, so need toggle back on the tournament mode
      setPlayKind(playerRole)

      // @ts-expect-error
      timer.current = setInterval(() => {
        fetchActiveGameList(selectedDifficulty)
      }, 1000)

      return () => {
        if (timer.current) {
          // @ts-ignore
          clearInterval(timer.current)
        }
      }
    }, [fetchActiveGameList, selectedDifficulty, playerRole, setPlayKind, teardown])
  )

  const openDeleteModal = (gameId: string) => {
    setDeleteModalVisible(true)
    setCurrentGameId(gameId)
  }

  const onDeleteGame = async () => {
    setDeleteModalVisible(false)
    const userId = store.session.user?._id
    if (userId) {
      await store.communication.terminateGame(currentGameId, userId)
    }
  }

  const viewPPSchedule = throttle(async () => {
    try {
      const PP_SCHEDULE_URL = 'https://portal.pokerpower.com/poker-power-play-community-games'
      // const PP_SCHEDULE_URL = 'https://pokerpower.com/for-players/'

      // const isUrlValid = await Linking.canOpenURL(PP_SCHEDULE_URL)
      // if (isUrlValid) {
      //   await Linking.openURL(PP_SCHEDULE_URL)
      // }
      const result = await WebBrowser.openBrowserAsync(PP_SCHEDULE_URL)
      logger.info('viewPPSchedule result', result)
    } catch (error) {
      logger.error('Cannot open PP Schedule')
    }
  }, 1000)

  const Header = useMemo(() => {
    return (
      <View style={styles.screenHeaderContainer}>
        <View>
          <TouchableOpacity onPress={viewPPSchedule} style={styles.ppScheduleButton}>
            <LinearGradient
              colors={['#49CDF2', '#9444FB']}
              start={[0, 1]}
              end={[1, 0]}
              style={styles.viewPPScheduleButton}
            >
              <Text tx="activeGameScreen.viewSchedule" allowFontScaling={false} style={styles.ppScheduleText} />
              <Image source={calendar} resizeMode="contain" style={styles.calIcon} />
            </LinearGradient>
          </TouchableOpacity>
        </View>
        {store.session.isTeacher && (
          <TouchableOpacity
            onPress={() => {
              router.navigate(`/game/create/tournament?accessControl=${TournamentAccessControl.community}`)
            }}
            style={styles.plusIcon}
          >
            <Image source={createTournament} style={{ width: 25, height: 25 }} />
          </TouchableOpacity>
        )}
      </View>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGames.length])

  const EmptyGameList = () => {
    return (
      <View style={styles.emptyContainer}>
        <Text allowFontScaling={false} style={styles.emptyText}>
          {translate('activeGameScreen.noCurrentGame')}
        </Text>
      </View>
    )
  }

  const navToLobby = useCallback(() => {
    router.navigate('/(root)/(tabs)')
  }, [])

  return (
    <View style={styles.container}>
      <NavigationHeader
        title="Community Games"
        customStyle={{ backgroundColor: 'transparent' }}
        navigate={navToLobby}
      />
      <FlatList
        data={activeGames}
        extraData={activeGames}
        numColumns={2}
        ListHeaderComponent={Header}
        ListEmptyComponent={<EmptyGameList />}
        renderItem={({ item: game }) => {
          if (!game.tournament) {
            return null
          }
          const tournament = game.tournament
          const table = game.snapshot?.table[0]
          if (!table) return null
          const currentTableState = table.tableState
          const isTableFull = table.isTableFull
          const players: Player[] = game.forPlayers?.[0] ? game.forPlayers[0].players : []
          const observerPlayers = players.filter((player) => {
            return player.state.tag === PlayerStates.Observer.tag
          })
          // Note: This is an offset to match the 15 minutes on game engine side and 5 min for dev
          const validDuration = store.environment.isDev()
            ? table.gameStartTime + 6 * 60 * 1000 - 30000
            : table.gameStartTime + 16 * 60 * 1000 - 30000
          const currentTimestamp = Date.now()
          const isValidDuration = currentTimestamp < validDuration
          const isValidTime =
            table.gameStartTime !== 0 ? isValidDuration && table.state.tag !== GameStates.ReadyToPlay.tag : true
          const isGameOver = currentTableState === GameStates.Finished.tag
          const isLocked = game.lock?.isLocked || isGameOver
          logger.info('game', table.state.tag, game._id, game.lock, table.state)
          return (
            <View style={{ flex: 0.5 }}>
              <GameCard
                isTeacher={store.session.isTeacher}
                title={tournament.name ?? ''}
                difficulty={tournament.difficulty ?? ''}
                seatLeft={9 - players.length + observerPlayers.length}
                color={tournament.color ?? 'black'}
                isTableFull={isTableFull}
                isGameOver={isGameOver}
                isLocked={isLocked || currentTableState === GameStates.Finished.tag}
                openDeleteModal={(id: string) => openDeleteModal(id)}
                isValidDuration={
                  table.gameStartTime !== 0 ? isValidDuration && table.state.tag !== GameStates.ReadyToPlay.tag : true
                }
                gameId={game._id}
                onRegister={async () => {
                  logger.info('GameCard onRegister', tournament.name)
                  setChannelName(game.channelName)
                  registeredGameActionTime.current = tournament.actionTime
                    ? tournament.actionTime * 1000
                    : store.game.actionTime
                  store.game.setRemainingTime(tournament.blindTimer ? tournament.blindTimer * 60 : store.game.blindTime)
                  selectedCurrentTournament.current = game
                  await onRegisterPrompt(isValidTime, isLocked, tournament.name ?? '')
                }}
              />
            </View>
          )
        }}
        keyExtractor={(item) => item._id}
        style={{ marginHorizontal: 5, flex: 1 }}
      />
      <ActionModal
        key={'userRole'}
        onSelect={(role: string) => onSelectRole(role)}
        message={translate('join.confirmMessageBeforeJoinTheTable') ?? ''}
        visible={visible}
        buttonTitle1={translate('common.observe')}
        buttonTitle2={translate('common.play')}
        onModalHide={() => setVisible(false)}
        onClose={() => setVisible(false)}
        type={'userRole'}
        shouldHideSecondButton={true}
      />
      <ActionModal
        key={'DeleteTournamentGameModal'}
        onAction={onDeleteGame}
        onModalHide={() => setDeleteModalVisible(false)}
        onClose={() => setDeleteModalVisible(false)}
        message={translate('activeGameScreen.deleteConfirmation')}
        visible={deleteModalVisible}
      />
    </View>
  )
})
